import { Alert, Button, Chip, TextLink } from '@kontentino/ui';
import AccordionWidget from 'app/modules/posts/components/AccordionWidget';
import AccordionWidgetIcon from 'app/modules/posts/components/accordionWidget/AccordionWidgetIcon';
import AccordionWidgetTitle from 'app/modules/posts/components/accordionWidget/AccordionWidgetTitle';
import { usePostQuery } from 'app/modules/posts/modules/post/hooks/usePostQuery';
import { isLocalPostLocked } from 'app/modules/posts/utils/globalPosts';
import clsx from 'clsx';
import Link from 'components/shared/Link';
import routes, { modalRoutes } from 'constants/routes';
import React, { FC } from 'react';
import useBoolean from 'utils/hooks/useBoolean';
import { createSearchFromParams, routePathToUrl } from 'utils/url';
import PostPendingChangesDetailModal from '../postPendingChangesDetail/PostPendingChangesDetailModal';
import { useMutation } from 'react-query';
import { useEffectOnce } from 'utils/hooks/useEffectOnce';
import Skeleton from 'react-loading-skeleton';
import { PermissionsGate } from '@kontentino/react-permissions-gate';
import { UserPermission } from 'constants/userPermission';
import UserPermissionGate from 'components/UserPermissionGate';
import { HyperText } from '@kontentino/kontentino-post-previews';
import { useTranslation } from 'react-i18next';
import PostApi from 'api/post';
import ImageTranslationPanel from 'app/modules/posts/modules/translation/ImageTranslationPanel';
import PostsConfig from 'app/config/posts';
import { SUBTYPE, TYPE } from '@kontentino/kontentino-constants/Posts';

export type Props = {
  id: number;
  accordionProps?: Partial<React.ComponentProps<typeof AccordionWidget>>;
};

const PostOverviewLocalPost: FC<Props> = ({ id, accordionProps }) => {
  const { postDetail } = usePostQuery(id);
  const [isPendingChangesModalOpen, setIsPendingChangesModalOpen] =
    useBoolean(false);
  const { t } = useTranslation();

  const {
    data: pendingChanges,
    mutate: getPatchesMutate,
    isLoading: pendingChangesLoading,
  } = useMutation(() => PostApi.getPatches(id));

  useEffectOnce(() => {
    getPatchesMutate();
  });

  if (postDetail.data?.isGlobal === false) {
    return (
      <>
        <AccordionWidget
          icon={<AccordionWidgetIcon variant="localPost" />}
          title={
            <AccordionWidgetTitle
              variant="localPost"
              className="tw-flex tw-justify-between"
            />
          }
          className="tw-flex tw-flex-col"
          titleSuffix={(isOpen) =>
            !isOpen &&
            pendingChanges?.length &&
            pendingChanges.length > 0 && (
              <Chip variant="darkGrey" className="tw-px-2">
                {t('globalPages.pendingChange', {
                  count: pendingChanges.length,
                })}
              </Chip>
            )
          }
          {...accordionProps}
        >
          {pendingChangesLoading && (
            <Skeleton count={1} height={80} className="tw-mb-4" />
          )}
          {!pendingChangesLoading &&
            !!pendingChanges &&
            pendingChanges.length > 0 && (
              <PermissionsGate
                scopes={UserPermission.POST_APPLY_GLOBAL_CHANGES}
              >
                <Alert
                  variant="warning"
                  className="tw-mb-4"
                  title={t('globalPages.thereAreSomePendingChangesToYourPost')}
                >
                  <TextLink>
                    <Button
                      variant="plain"
                      size="small"
                      className="tw-text-sm tw-font-medium"
                      onClick={setIsPendingChangesModalOpen.on}
                    >
                      {t('globalPages.reviewPendingChanges')}
                    </Button>
                  </TextLink>
                </Alert>
              </PermissionsGate>
            )}

          {isLocalPostLocked(postDetail.data) && (
            <Alert
              variant="warning"
              className="tw-mb-4"
              title={t('globalPages.thisPostIsLocked')}
            >
              {t('globalPages.waitUntilTheGlobalProfileApprovesThePost')}
            </Alert>
          )}

          <UserPermissionGate
            scopes={[
              UserPermission.POST_EDIT_TEXT,
              UserPermission.POST_EDIT_ASSETS,
            ]}
            exact={false}
          >
            <div className="tw-mb-4">
              <ImageTranslationPanel
                postId={id}
                hasImages={
                  ([TYPE.PHOTO, TYPE.GALLERY] as number[]).includes(
                    postDetail.data.type,
                  ) &&
                  postDetail.data.attachments?.cards?.some(
                    (card) => card.medium?.type === 'image',
                  )
                }
                hasText={
                  !([SUBTYPE.INSTAGRAM_STORY] as number[]).includes(
                    postDetail.data.subtype,
                  ) && !!postDetail.data.text
                }
                hasFirstComment={
                  !!postDetail.data.attachments?.firstComment?.text &&
                  postDetail.data.page.type in
                    PostsConfig.FIRST_COMMENT_CHARACTER_LIMIT
                }
                isLocked={!!isLocalPostLocked(postDetail.data)}
                pageType={postDetail.data.page?.type}
                pageId={postDetail.data.page?.id}
              />
            </div>
          </UserPermissionGate>

          <Link
            target="_blank"
            to={routePathToUrl({
              pathname: routes.POSTS_CALENDAR,
              hash: modalRoutes.POST_DETAIL,
              search: createSearchFromParams({
                id: postDetail.data.globalPost?.id,
              }),
            })}
          >
            <Button variant={'secondary'} className="tw-mb-4 tw-w-full">
              {t('globalPages.viewGlobalPost')}
            </Button>
          </Link>
          <div className="tw-flex tw-flex-col tw-text-sm">
            {postDetail.data.note2 && (
              <div className="tw-flex tw-flex-col tw-gap-y-2">
                <div className="tw-font-semibold">
                  {t('globalPages.postInstructions')}
                </div>
                <HyperText
                  options={{
                    ellipsis: true,
                  }}
                >
                  {postDetail.data.note2}
                </HyperText>
              </div>
            )}
          </div>

          {!postDetail.data.note2 && (
            <div
              className={clsx(
                'tw-mt-3 tw-flex tw-h-full tw-justify-center tw-gap-x-1 tw-text-grayscale-100',
              )}
            >
              <span className="tw-text-sm tw-font-regular">
                {t('globalPages.youDontHaveAnyInstructionsYet')}
              </span>
            </div>
          )}
        </AccordionWidget>

        {isPendingChangesModalOpen && (
          <PostPendingChangesDetailModal
            postId={id}
            isOpen={isPendingChangesModalOpen}
            onClose={() => {
              setIsPendingChangesModalOpen.off();
              getPatchesMutate();
            }}
          />
        )}
      </>
    );
  }

  return null;
};

export default PostOverviewLocalPost;
